<template>
  <div class="unites-soins">
    <div class="unites-entete">
      <p class="text-grey mb-0">Chaque unité regroupe ses soignants et ses chambres. L’armoire est facultative : sans armoire dédiée, les sorties se font depuis la pharmacie centrale.
        Les soignants s'affectent depuis Personnel, les chambres depuis Prestations &amp; Tarifs.</p>
      <button class="btn-primary btn-radius" type="button" @click="ouvrir()"><i class="ic-plus h-13p"></i> Nouvelle unité</button>
    </div>
    <div v-if="erreur && !formulaire" class="alert alert-danger mt-3" role="alert">{{ erreur }}</div>
    <div v-if="chargement" class="text-grey mt-3">Chargement des unités…</div>
    <div v-else class="table-responsive mt-3">
      <table class="table unites-table">
        <thead><tr><th>Code</th><th>Unité</th><th>Type</th><th>Armoire</th><th>Responsable</th><th>Statut</th><th></th></tr></thead>
        <tbody>
          <tr v-for="unite in unites" :key="unite.id">
            <td>{{ unite.code }}</td><td>{{ unite.name }}</td><td>{{ nomType(unite.service_type) }}</td>
            <td>{{ unite.stock_name || (unite.stock_id ? 'Armoire indisponible' : 'Pharmacie centrale') }}</td><td>{{ nomPersonne(unite.head_user_id) }}</td>
            <td><span :class="unite.is_active ? 'badge badge-success' : 'badge badge-secondary'">{{ unite.is_active ? 'Active' : 'Inactive' }}</span></td>
            <td><button type="button" class="btn btn-outline-primary btn-sm" @click="ouvrir(unite)">Modifier</button></td>
          </tr>
          <tr v-if="!unites.length"><td colspan="7" class="text-grey">Aucune unité de soins pour le moment.</td></tr>
        </tbody>
      </table>
    </div>

    <div v-if="formulaire" class="unites-fenetre" role="dialog" aria-modal="true" aria-labelledby="unite-titre">
      <form class="unites-fenetre-contenu" @submit.prevent="enregistrer">
        <div class="unites-entete">
          <h4 id="unite-titre" class="mb-0">{{ formulaire.id ? 'Modifier l’unité' : 'Nouvelle unité de soins' }}</h4>
          <button type="button" class="btn btn-link" aria-label="Fermer" @click="fermer">×</button>
        </div>
        <div class="form-row">
          <div class="form-group col-md-8"><label for="unite-nom">Nom *</label>
            <input id="unite-nom" v-model.trim="formulaire.name" class="input-secondary-light" maxlength="150" required></div>
          <div class="form-group col-md-4"><label for="unite-code">Code court *</label>
            <input id="unite-code" v-model.trim="formulaire.code" class="input-secondary-light" maxlength="20" pattern="[A-Za-z0-9_-]+" required></div>
        </div>
        <div class="form-row">
          <div class="form-group col-md-6"><label for="unite-type">Type d’activité</label>
            <select id="unite-type" v-model="formulaire.service_type" class="input-secondary-light">
              <option v-for="type in types" :key="type.value" :value="type.value">{{ type.label }}</option>
            </select></div>
          <div class="form-group col-md-6"><label for="unite-armoire">Stock de sortie</label>
            <select id="unite-armoire" v-model="formulaire.armoire" class="input-secondary-light">
              <option value="">Pharmacie centrale (sans armoire dédiée)</option>
              <option v-if="!formulaire.id" value="nouvelle">Créer l’armoire de cette unité</option>
              <option v-for="armoire in armoiresLibres" :key="armoire.id" :value="String(armoire.id)">{{ armoire.nom }}</option>
            </select>
            <small v-if="formulaire.armoire === 'nouvelle'" class="text-grey">« Armoire {{ formulaire.name || '…' }} » sera créée dans le stock ; la pharmacie la dotera.</small></div>
        </div>
        <div class="form-row">
          <div class="form-group col-md-6"><label for="unite-responsable">Responsable / major</label>
            <select id="unite-responsable" v-model="formulaire.head_user_id" class="input-secondary-light">
              <option :value="null">Non renseigné</option>
              <option v-for="personne in soignants" :key="personne.id" :value="personne.id">{{ personne.name }}</option>
            </select></div>
          <div v-if="formulaire.id" class="form-group col-md-6"><label for="unite-statut">Statut</label>
            <select id="unite-statut" v-model="formulaire.is_active" class="input-secondary-light">
              <option :value="true">Active</option><option :value="false">Inactive</option>
            </select>
            <small class="text-grey">Une unité qui a des patients installés, des chambres ou du stock ne peut pas être désactivée.</small></div>
        </div>
        <div v-if="erreur" class="alert alert-danger" role="alert">{{ erreur }}</div>
        <div class="text-right">
          <button type="button" class="btn btn-outline-secondary mr-2" @click="fermer">Annuler</button>
          <button class="btn-primary btn-radius" type="submit" :disabled="enregistrement">{{ enregistrement ? 'Enregistrement…' : 'Enregistrer' }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import UnitesSoinsApi from '@/api/stock/_unites-soins'
import AccessibiliteApi from '@/api/parametres/_accessibilite'
import { estSoignant } from './unites-soins-utils'

export default {
  name: 'UnitesSoins',
  data() {
    return {
      unites: [], armoires: [], personnel: [], chargement: true, enregistrement: false, erreur: '', formulaire: null,
      types: [
        { value: 'INPATIENT', label: 'Hospitalisation' }, { value: 'OUTPATIENT', label: 'Consultations externes' },
        { value: 'EMERGENCY', label: 'Urgences' }, { value: 'TECHNICAL_PLATFORM', label: 'Plateau technique' }
      ]
    }
  },
  computed: {
    soignants() { return this.personnel.filter(estSoignant) },
    // Une armoire ne sert qu'à une unité : on propose celles qui sont libres, plus celle de l'unité modifiée.
    armoiresLibres() {
      const id = this.formulaire && this.formulaire.id
      return this.armoires.filter(armoire => !this.unites.some(unite =>
        Number(unite.stock_id) === Number(armoire.id) && unite.id !== id))
    }
  },
  mounted() { this.charger() },
  methods: {
    nomType(valeur) { const type = this.types.find(item => item.value === valeur); return type ? type.label : valeur },
    nomPersonne(id) { const personne = this.personnel.find(item => Number(item.id) === Number(id)); return personne ? personne.name : 'Non renseigné' },
    async charger() {
      this.chargement = true; this.erreur = ''
      try {
        const [unites, armoires, personnel] = await Promise.all([
          UnitesSoinsApi.liste(), UnitesSoinsApi.armoires(), AccessibiliteApi.getPersonnels().catch(() => [])
        ])
        this.unites = unites; this.armoires = armoires; this.personnel = personnel || []
      } catch (error) { this.erreur = UnitesSoinsApi.messageErreur(error) }
      finally { this.chargement = false }
    },
    ouvrir(unite) {
      this.erreur = ''
      this.formulaire = unite
        ? { id: unite.id, code: unite.code, name: unite.name, service_type: unite.service_type, armoire: unite.stock_id ? String(unite.stock_id) : '',
          head_user_id: unite.head_user_id || null, is_active: !!unite.is_active }
        : { code: '', name: '', service_type: 'INPATIENT', armoire: '', head_user_id: null, is_active: true }
    },
    fermer() { this.formulaire = null; this.erreur = '' },
    donnees() {
      const f = this.formulaire
      const donnees = { id: f.id, code: f.code, name: f.name, service_type: f.service_type, head_user_id: f.head_user_id,
        stock_id: f.armoire && f.armoire !== 'nouvelle' ? Number(f.armoire) : null }
      if (f.id) donnees.is_active = f.is_active
      else if (f.armoire === 'nouvelle') donnees.create_stock = true
      return donnees
    },
    async enregistrer() {
      this.enregistrement = true; this.erreur = ''
      try {
        await UnitesSoinsApi.enregistrer(this.donnees())
        this.formulaire = null
        await this.charger()
      } catch (error) { this.erreur = UnitesSoinsApi.messageErreur(error) }
      finally { this.enregistrement = false }
    }
  }
}
</script>

<style scoped>
.unites-entete { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px }
.unites-table th { background: #f3f6fc; color: var(--ui-text-secondary, #65728b); border: 0; white-space: nowrap }
.unites-table td { vertical-align: middle; border-top: 1px solid #edf0f6 }
.unites-fenetre { position: fixed; z-index: 1050; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(30, 40, 64, .45); padding: 16px }
.unites-fenetre-contenu { background: #fff; border-radius: 16px; width: 100%; max-width: 760px; max-height: 90vh; overflow-y: auto; padding: 24px }
.unites-fenetre-contenu .form-group label { display: block }
@media (max-width: 650px) { .unites-entete { flex-direction: column } }
</style>
